import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Mail, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Check your inbox for reset instructions.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative z-10">
      <Card className="w-full max-w-md glass border-white/10" data-testid="forgot-password-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src="/logo.png" alt="Sacred Celestial Flow" className="w-20 h-20 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Forgot your password?</CardTitle>
          <CardDescription className="text-mystic-200/70">
            Enter your email and we'll send you a secure reset link.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-success">
              <div className="p-4 rounded-lg bg-mystic-300/10 border border-mystic-300/30">
                <p className="text-mystic-100 text-sm">
                  If <span className="text-mystic-300">{email}</span> is registered, a reset link has been sent. The link expires in 1 hour.
                </p>
              </div>
              <p className="text-mystic-200/70 text-xs">
                Didn't receive it? Check your spam folder or try again in a few minutes.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-mystic-100">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-mystic-300" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="forgot-password-email-input"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-mystic-900/50 border-white/10 focus:border-mystic-300 text-white placeholder:text-white/30"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="forgot-password-submit-btn"
                disabled={loading}
                className="w-full bg-mystic-300 hover:bg-mystic-200 text-white font-medium py-3 rounded-full shadow-[0_0_15px_rgba(157,78,221,0.4)] transition-all duration-300"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
          )}

          <Link
            to="/login"
            data-testid="forgot-password-back-to-login"
            className="flex items-center justify-center gap-2 text-sm text-mystic-300 hover:text-mystic-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to sign in
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
